import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { outlookConfig } from './utils/outlookConfig';
import { patchStorefrontCalendarIntegrations, patchStorefrontAppointmentCalendar } from '../../../../../api/storefront/calendar-sync/calendarSync';
import { parseAppointmentDate } from './utils/parseAppointmentDate';

let msalInstance = null;

const initializeMsal = async () => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: outlookConfig.clientId,
        authority: outlookConfig.authority,
        redirectUri: outlookConfig.redirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    });
    await msalInstance.initialize();
  }
  return msalInstance;
};

const getAuthToken = async () => {
  const msal = await initializeMsal();

  try {
    const account = msal.getAllAccounts()[0];
    if (!account) {
      throw new InteractionRequiredAuthError();
    }

    const silentRequest = {
      scopes: outlookConfig.scopes,
      account,
    };

    const response = await msal.acquireTokenSilent(silentRequest);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const interactiveRequest = {
        scopes: outlookConfig.scopes,
      };
      const response = await msal.acquireTokenPopup(interactiveRequest);
      return response.accessToken;
    }
    throw error;
  }
};

export const connectOutlookCalendar = async (userId, companyId) => {
  try {
    const msal = await initializeMsal();
    await msal.initialize();

    const loginResponse = await msal.loginPopup({
      scopes: outlookConfig.scopes,
      prompt: 'select_account',
    });

    if (!loginResponse?.account) {
      throw new Error('Failed to get account information');
    }

    const token = await getAuthToken();
    localStorage.setItem('outlookCalendarToken', token);
    localStorage.setItem(
      'outlookCalendarTokenExpiry',
      new Date(Date.now() + 3600 * 1000).toISOString()
    );

    await patchStorefrontCalendarIntegrations({
      companyId,
      clientId: userId,
      outlook: true,
    });

    return { success: true };
  } catch (error) {
    console.error('Error connecting to Outlook:', error);
    return { success: false, error: error.message };
  }
};

export const syncWithOutlookCalendar = async (appointment, userId, isUpdate = false) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Not authenticated with Outlook');
    }

    const startDate = parseAppointmentDate(appointment?.dateTime);
    if (!startDate) {
      throw new Error('Invalid appointment: missing dateTime');
    }

    const parsedEnd = parseAppointmentDate(appointment.endTime);
    const endDate = parsedEnd
      || (appointment.duration
        ? new Date(startDate.getTime() + appointment.duration * 60000)
        : new Date(startDate.getTime() + 60 * 60000));

    if (!endDate || Number.isNaN(endDate.getTime())) {
      throw new Error('Invalid appointment end date');
    }

    const event = {
      subject: `Appointment with ${appointment.clientName || 'Client'}`,
      start: {
        dateTime: startDate.toISOString(),
        timeZone: 'UTC',
      },
      end: {
        dateTime: endDate.toISOString(),
        timeZone: 'UTC',
      },
      body: {
        contentType: 'HTML',
        content: `
          <p><strong>Client:</strong> ${appointment.clientName || 'N/A'}</p>
          <p><strong>Service:</strong> ${appointment.serviceName || appointment.service?.name || 'N/A'}</p>
          ${appointment.notes ? `<p><strong>Notes:</strong> ${appointment.notes}</p>` : ''}
        `,
      },
    };

    const eventId = appointment.outlookEventId;
    const url =
      isUpdate && eventId
        ? `https://graph.microsoft.com/v1.0/me/calendar/events/${encodeURIComponent(eventId)}`
        : 'https://graph.microsoft.com/v1.0/me/calendar/events';

    const response = await fetch(url, {
      method: isUpdate && eventId ? 'PATCH' : 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || error.error?.message || 'Failed to sync with Outlook calendar');
    }

    const result = await response.json();
    const companyId = appointment.companyId || localStorage.getItem('clientCompanyId');
    if (appointment.id && companyId && result.id) {
      const now = new Date().toISOString();
      const patch = {
        outlookEventId: result.id,
        lastSyncedAt: now,
        outlookLastSyncedAt: now,
      };
      if (isUpdate && eventId && eventId !== result.id) {
        patch.previousOutlookEventId = eventId;
      }
      try {
        await patchStorefrontAppointmentCalendar({
          companyId,
          appointmentId: appointment.id,
          patch,
        });
      } catch (updateError) {
        console.error('Failed to update appointment with Outlook event ID:', updateError);
      }
    }

    return { success: true, eventId: result.id };
  } catch (error) {
    console.error('Error syncing with Outlook:', error);
    return { success: false, error: error.message };
  }
};

export const disconnectOutlookCalendar = async (userId, companyId) => {
  try {
    const msal = await initializeMsal();
    const account = msal.getAllAccounts()[0];

    if (account) {
      await msal.logoutPopup({
        account,
        postLogoutRedirectUri: window.location.origin,
      });
    }

    localStorage.removeItem('outlookCalendarToken');
    localStorage.removeItem('outlookCalendarTokenExpiry');

    await patchStorefrontCalendarIntegrations({
      companyId,
      clientId: userId,
      outlook: false,
    });

    return { success: true };
  } catch (error) {
    console.error('Error disconnecting from Outlook:', error);
    return { success: false, error: error.message };
  }
};
